import { useState, useEffect, useCallback, Fragment } from 'react';
import { usePortalStore } from '@/store/portal-store.js';
import { makePortalInstance } from '@edison/api-client-react';
import { CheckCircle2, AlertTriangle, Download } from 'lucide-react';
import { InlineError } from '@/components/ui/inline-error.js';

// Portal endpoints are not in the OpenAPI spec; the response shape is the locked claim snapshot
// (services/claim-snapshot.ts). Auditors see the exact lodged figures — no editable rate,
// no per-person salaries, no per-entry timesheets.
interface SnapshotActivity {
  name: string;
  description: string | null;
  ipOwnership: string;
  activityType?: string; // absent on locked snapshots captured before this field existed
  rdHours: number;
  rdExpenditure: number;
}
interface SnapshotProject {
  name: string;
  tfaDetermination: string | null;
  problemStatement: string | null;
  description?: string | null;
  activities: SnapshotActivity[];
}
// Synthetic row name for R&D time not yet attached to a real activity — matches
// api-server/src/services/claim-snapshot.ts. Used by CategoryChip to force the "Unallocated"
// chip on legacy rows where `activityType` is undefined.
const UNALLOCATED_ACTIVITY_NAME = 'Unallocated to activity';

const CATEGORY_LABEL: Record<string, string> = {
  cloud: 'Cloud infrastructure', llm_tokens: 'LLM usage',
  software_license: 'Software licences', hardware: 'Hardware', other: 'Other',
};
interface PortalExpenditure {
  incomeYear: string;
  status: string;
  lockedAt: string | null;
  mode?: string;
  totalRdHours: number;
  totalRdExpenditure: number;
  totalNonLabourExpenditure: number;
  nonLabourBreakdown?: Array<{ category: string; source: string; amount: number }>;
  grandTotal: number;
  projects: SnapshotProject[];
  // Structural counts, computed independently of the (possibly stored) snapshot — absent on
  // older API responses, hence optional. See services/claim-composition.ts on the API server.
  composition?: { claimedProjectCount: number; excludedNonRdProjectCount: number };
}

interface GlReconVendor {
  vendor: string; edisonAmount: number; glAmount: number | null;
  varianceAmount: number | null; variancePct: number | null;
  mappedAccounts: string[]; unmapped: boolean;
  category: string | null;
  sourceKind: 'synced' | 'manual' | 'mixed';
  members?: Array<{ vendor: string; edisonAmount: number }>;
}
interface GlReconciliation {
  basis: string; incomeYear: string;
  labour: {
    payrollTotal: number; monthsCovered: number;
    capturedAllHoursLabour: number; rdLabour: number;
    ratios: { capturedVsPayroll: number | null; rdVsPayroll: number | null };
    overCapture: boolean;
    byMonth: Array<{ month: string; payroll: number; capturedLabour: number }>;
  };
  nonLabour: GlReconVendor[];
  meta: { lastXeroSyncAt: string | null; hasSnapshotData: boolean };
}

// GET /api/portal/labour-timesheet — advisor/auditor GRANT only (403 for anonymous/due_diligence).
// See artifacts/api-server/src/services/labour-timesheet.ts for the source shape. `mode` is the
// internal R&D basis-mode key (e.g. 'business_hours') and, like PortalExpenditure.mode above,
// MUST NEVER be rendered anywhere on this page — it is read but intentionally unused in JSX.
interface RateCardComponents {
  baseSalary: number; super: number; payrollTax: number; insurances: number; annualHours: number;
}
interface RateCardEntry {
  userId: string; name: string; hourlyRate: number;
  components: RateCardComponents | null;
  componentsMismatch?: boolean;
}
interface LabourTimesheetContributor {
  userId: string; name: string; rdHours: number; rdExpenditure: number;
  // Set only when true — rdHours > 0 with rdExpenditure exactly 0 because no rate is configured
  // for this contributor yet (see labour-timesheet.ts). Render "no rate", never a misleading "$0".
  unrated?: boolean;
}
interface LabourTimesheetProject { projectId: string; projectName: string; contributors: LabourTimesheetContributor[]; }
interface LabourTimesheetDailyRow {
  date: string; userId: string; name: string; projectId: string; projectName: string;
  rdHours: number; rdExpenditure: number;
}
interface LabourTimesheetData {
  basis: string;
  mode: string; // internal — never rendered
  totalRdExpenditure: number;
  reconciliation: { lodgedTotal: number; computedTotal: number; delta: number };
  byProject: LabourTimesheetProject[];
  dailyRows: LabourTimesheetDailyRow[];
  rateCard: RateCardEntry[];
}

// Distinct dark-theme hues for the top contributors by claim-wide R&D hours; everyone past the
// palette shares a neutral grey. Assignment is computed once, claim-wide, so a person's colour is
// consistent across every project card (spec §8 ambiguity H).
// Muted (/70) so a full-width segment reads as an accent, not a banner; no red/rose — a large red
// block on a finance page reads as an error state, not a person.
const PERSON_COLORS = ['bg-blue-500/70', 'bg-emerald-500/70', 'bg-violet-500/70', 'bg-amber-500/70', 'bg-cyan-500/70', 'bg-slate-400/70'] as const;
const PERSON_OVERFLOW = 'bg-gray-600/70';

// Contributor names fall back to emails in the labour service — show just the local part in card
// legends ("khush", not "khush@polusai.com.au"); full identity stays in the timesheet/rate card.
function displayName(name: string): string {
  return name.includes('@') ? (name.split('@')[0] as string) : name;
}

function buildPersonPalette(labour: LabourTimesheetData | null): Map<string, string> {
  const byUser = new Map<string, number>();
  if (labour) {
    for (const p of labour.byProject) {
      for (const c of p.contributors) {
        byUser.set(c.userId, (byUser.get(c.userId) ?? 0) + c.rdHours);
      }
    }
  }
  const ranked = [...byUser.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  const map = new Map<string, string>();
  ranked.forEach(([userId], i) => map.set(userId, PERSON_COLORS[i] ?? PERSON_OVERFLOW));
  return map;
}

function formatCurrency(n: number) {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(n);
}

// $/hr wants cent precision (e.g. "$95.00/hr") — the header totals above deliberately round to
// whole dollars, but a rate card that hides cents could misrepresent the substantiated rate.
function formatRate(n: number) {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(n);
}

// The CSV downloads used to be assembled client-side into a Blob + `<a download>` — that only
// ever worked in Chromium. Real Safari ignores the `download` filename on a blob object URL and
// saves the file under the blob's UUID with no extension (unfixable client-side). The fix
// (fix/server-side-csv-export) is to have the API server generate the CSV and return it with a
// `Content-Disposition: attachment; filename="..."` header — every browser, including Safari,
// respects that. That only works for a REAL browser-issued GET, so these exports navigate the
// browser directly to the server endpoint instead of fetching + rebuilding a Blob.
//
// A plain navigation can't set custom request headers (Authorization / X-Access-Token /
// X-Claim-Id). For grant-mode users that used to fall back to Clerk's `__session` cookie — but
// that handshake does not survive crossing from the CloudFront frontend origin to the raw
// execute-api domain (verified live: the navigation redirects to execute-api and 422s), so
// grant-mode users could not download at all. The fix (fix/csv-download-token): mint a short-lived
// (60s), signed one-shot token via an authenticated POST — which DOES carry Bearer/X-Claim-Id
// normally, no navigation involved — then navigate to the CSV endpoint with that token in `?dt=`.
// Applied uniformly to both portal modes (not just grant mode) so there is a single code path; see
// artifacts/api-server/src/services/export-token.ts + routes/audit-portal.ts (dtAuth) for the
// server-side verification.
const apiBase = typeof window !== 'undefined' ? (import.meta.env['VITE_API_BASE_URL'] ?? '') : '';

function ExportCsvButton({ onClick, label, title }: { onClick: () => void; label: string; title?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={title}
      className="inline-flex items-center gap-1.5 rounded-md border border-gray-800 bg-gray-900 px-2.5 py-1 text-xs text-gray-300 hover:bg-gray-800 hover:text-white transition-colors shrink-0"
    >
      <Download className="h-3.5 w-3.5" aria-hidden="true" />
      Export to CSV
    </button>
  );
}

// Compact dark-theme <select> styling for the footer filter rows — mirrors ExportCsvButton's
// border-gray-800/bg-gray-900, text-xs convention (and portal-evidence.tsx's selectClass idiom).
const FILTER_SELECT_CLASS = 'rounded-md border border-gray-800 bg-gray-900 px-2 py-1 text-xs text-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500';

// Rule Zero gate for rate composition: only show the bracketed breakdown when it actually ties to
// the stored hourly rate. A present-but-mismatched composition is flagged, never shown as if valid.
function RateComposition({ entry }: { entry: RateCardEntry }) {
  if (entry.components) {
    const c = entry.components;
    return (
      <span className="text-xs text-gray-400 tabular-nums">
        ( = salary {formatCurrency(c.baseSalary)} + super {formatCurrency(c.super)} + payroll tax{' '}
        {formatCurrency(c.payrollTax)} + insurances {formatCurrency(c.insurances)} ) ÷{' '}
        {c.annualHours.toLocaleString('en-AU')} hrs
      </span>
    );
  }
  if (entry.componentsMismatch) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-amber-400">
        <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden="true" />
        Composition unavailable
      </span>
    );
  }
  // No per-person components loaded yet (not a mismatch) — show the standard methodology formula
  // so "how is the rate calculated" has an answer. This describes the method only, no invented
  // per-person figures, so it stays Rule Zero-safe.
  return (
    <span className="text-xs text-gray-500 italic">
      (annual salary + superannuation + workers compensation insurance) ÷ annual hours
    </span>
  );
}

function DeterminationBadge({ d }: { d: string | null }) {
  if (!d) return <span className="text-xs text-gray-500">No determination</span>;
  const eligible = d !== 'certain';
  const Icon = eligible ? CheckCircle2 : AlertTriangle;
  const cls = eligible ? 'text-emerald-400 border-emerald-900/50 bg-emerald-950/40' : 'text-amber-400 border-amber-900/50 bg-amber-950/40';
  const label = d === 'uncertain' ? 'Uncertain (eligible)'
    : d === 'partially_uncertain' ? 'Partially uncertain'
    : d === 'certain' ? 'Certain (ineligible)' : d;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${cls}`}>
      <Icon className="h-3 w-3" aria-hidden="true" />{label}
    </span>
  );
}

function SourcePill({ kind }: { kind: 'synced' | 'manual' | 'mixed' }) {
  const label = kind === 'synced' ? 'Synced · billing API' : kind === 'manual' ? 'Manual · no billing API' : 'Mixed';
  const dotCls = kind === 'synced' ? 'bg-emerald-400' : kind === 'manual' ? 'bg-gray-500' : 'bg-amber-400';
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-800 bg-gray-900 px-2 py-0.5 text-xs text-gray-300">
      <span className={`h-1.5 w-1.5 rounded-full ${dotCls}`} aria-hidden="true" />
      {label}
    </span>
  );
}

// Category chip for the per-project activities table — mirrors the determination/source pill
// idiom (icon-free, text label always present so status is never colour-only).
function CategoryChip({ activityType, name }: { activityType?: string; name: string }) {
  const kind = activityType === 'supporting'
    ? 'supp'
    : activityType === 'non_rd'
    ? 'nonrd'
    : activityType === 'unallocated' || name === UNALLOCATED_ACTIVITY_NAME
    ? 'unalloc'
    : 'core'; // 'core' and legacy undefined
  const label = kind === 'supp' ? 'Supporting' : kind === 'nonrd' ? 'Non-R&D' : kind === 'unalloc' ? 'Unallocated' : 'Core';
  const cls = kind === 'core'
    ? 'text-emerald-400 border-emerald-900/50'
    : kind === 'supp'
    ? 'text-blue-400 border-blue-900/50'
    : kind === 'nonrd'
    ? 'text-rose-400 border-rose-900/50'
    : 'text-gray-400 border-gray-800';
  return <span className={`inline-block rounded border px-1.5 py-0.5 text-[10px] whitespace-nowrap ${cls}`}>{label}</span>;
}

function AmberStatusLine({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-amber-900/50 bg-amber-950/40 px-3 py-2">
      <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0" aria-hidden="true" />
      <p className="text-xs text-amber-400">{text}</p>
    </div>
  );
}

interface ProjectCardProps {
  project: SnapshotProject;
  labourProject: LabourTimesheetProject | undefined;
  personColor: Map<string, string>;
}

// Per-project card: name + determination + totals (header), Iain's own-words description,
// a per-person contribution bar + legend (advisor/auditor grant only — omitted cleanly when
// `labourProject` is absent, e.g. anonymous/due_diligence 403), and a collapsed activities table
// with a category chip per row.
function ProjectCard({ project, labourProject, personColor }: ProjectCardProps) {
  const sumHours = project.activities.reduce((s, a) => s + a.rdHours, 0);
  const sumExpenditure = project.activities.reduce((s, a) => s + a.rdExpenditure, 0);

  const sortedContributors = labourProject
    ? [...labourProject.contributors].sort((a, b) => b.rdHours - a.rdHours)
    : [];
  const projTotal = sortedContributors.reduce((s, c) => s + c.rdHours, 0);

  return (
    <div className="rounded-lg border border-gray-800 overflow-hidden">
      <div className="border-b border-gray-800 bg-gray-900 px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-medium text-white">{project.name}</h2>
            {/* Iain authors these one-liners by hand — never generated/AI-filled. GitHub-boilerplate
                auto-descriptions render as if absent (Rule Zero founder-voice guard). */}
            {project.description && !/^Auto-created from GitHub repository/.test(project.description) && (
              <p className="text-xs text-gray-400 mt-1 max-w-xl">{project.description}</p>
            )}
            <div className="mt-1.5">
              <DeterminationBadge d={project.tfaDetermination} />
            </div>
          </div>
          <div className="text-right tabular-nums shrink-0">
            <p className="text-sm font-semibold text-white">{sumHours.toFixed(1)}</p>
            <p className="text-[10px] text-gray-500">R&amp;D hours</p>
            <p className="text-sm font-semibold text-white mt-1.5">{formatCurrency(sumExpenditure)}</p>
            <p className="text-[10px] text-gray-500">R&amp;D labour</p>
          </div>
        </div>
      </div>

      <div className="px-4 py-3 space-y-3">
        {/* Per-person contribution bar + legend — advisor/auditor grant only. Omitted entirely
            (not a zero-state) when labour is null/403; the activities table below still renders. */}
        {labourProject && projTotal > 0 && (
          <div>
            <div
              role="img"
              aria-label={`${project.name} contribution per person`}
              className="flex h-1.5 rounded-full overflow-hidden bg-gray-800"
            >
              {sortedContributors.map((c) => (
                <div
                  key={c.userId}
                  style={{ width: `${(c.rdHours / projTotal) * 100}%` }}
                  className={personColor.get(c.userId) ?? PERSON_OVERFLOW}
                  title={`${displayName(c.name)} ${c.rdHours.toFixed(1)}h`}
                />
              ))}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] mt-2">
              {sortedContributors.map((c) => (
                <span key={c.userId} className="inline-flex items-center gap-1.5">
                  <span
                    className={`inline-block h-2 w-2 rounded-full ${personColor.get(c.userId) ?? PERSON_OVERFLOW}`}
                    aria-hidden="true"
                  />
                  <b className="text-gray-200 font-medium">{displayName(c.name)}</b>
                  <span className="text-gray-500 tabular-nums">{c.rdHours.toFixed(1)} h</span>
                  {c.unrated ? (
                    <span className="text-amber-400/90">· no rate</span>
                  ) : (
                    <span className="text-gray-500 tabular-nums">· {formatCurrency(c.rdExpenditure)}</span>
                  )}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Activities detail — native <details>/<summary>, no sibling toggle button needed. */}
        <details className="border-t border-gray-800 pt-2 first:border-t-0 first:pt-0">
          <summary className="cursor-pointer text-xs text-blue-400 select-none">
            {project.activities.length} activities
          </summary>
          <div className="overflow-x-auto mt-2">
            <table className="w-full text-xs tabular-nums">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500">Activity</th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500">Category</th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">Hours</th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">$</th>
                </tr>
              </thead>
              <tbody>
                {project.activities.map((a) => (
                  <tr key={a.name} className="border-b border-gray-800 last:border-b-0">
                    <td className="px-2 py-2 text-left text-gray-300">{a.name}</td>
                    <td className="px-2 py-2"><CategoryChip activityType={a.activityType} name={a.name} /></td>
                    <td className="px-2 py-2 text-right text-gray-300">{a.rdHours.toFixed(1)}</td>
                    <td className="px-2 py-2 text-right text-gray-300">{formatCurrency(a.rdExpenditure)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      </div>
    </div>
  );
}

export function PortalExpenditurePage() {
  const token = usePortalStore((s) => s.token);
  const mode = usePortalStore((s) => s.mode);
  const [data, setData] = useState<PortalExpenditure | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [recon, setRecon] = useState<GlReconciliation | null>(null);
  const [reconLoading, setReconLoading] = useState(true);
  const [reconError, setReconError] = useState('');

  // Advisor/auditor GRANT-only substantiation surface. 403 (anonymous / due_diligence grant) is an
  // EXPECTED outcome for most viewers of this page — it hides the section with no error banner.
  // Any other failure is a real error and gets the standard error+retry state.
  const [labour, setLabour] = useState<LabourTimesheetData | null>(null);
  const [labourLoading, setLabourLoading] = useState(true);
  const [labourError, setLabourError] = useState('');
  const [labourForbidden, setLabourForbidden] = useState(false);

  // CSV export (mint-then-navigate, see the comment above `apiBase`). exportingKind tracks which
  // export the last failure belongs to, so the retry button in the InlineError banner re-attempts
  // the SAME export rather than guessing.
  const [exportError, setExportError] = useState('');
  const [exportingKind, setExportingKind] = useState<'labour-timesheet' | 'rate-card' | null>(null);

  // Collapsed-by-default detail sections — the vendor table, the daily timesheet, and the rate
  // card are all substantiation detail behind a one-line summary (spec §8 consolidation).
  const [xeroOpen, setXeroOpen] = useState(false);
  const [tsOpen, setTsOpen] = useState(false);
  const [rateOpen, setRateOpen] = useState(false);

  // Per-person daily timesheet — display-only filters. The full dataset is always exported to CSV
  // (it's the substantiation artefact); these only narrow what's rendered on screen.
  const [tsPersonFilter, setTsPersonFilter] = useState('all');
  const [tsProjectFilter, setTsProjectFilter] = useState('all');

  const fetchData = useCallback(() => {
    if (!token) return;
    const portal = makePortalInstance(token, { mode });
    setIsLoading(true);
    setError('');
    portal.get<{ data: PortalExpenditure }>('/api/portal/expenditure')
      .then((res) => setData(res.data))
      .catch(() => setError('Failed to load expenditure.'))
      .finally(() => setIsLoading(false));
  }, [token, mode]);

  // Independent, non-fatal fetch — a reconciliation failure must never blank the main page.
  const fetchRecon = useCallback(() => {
    if (!token) return;
    const portal = makePortalInstance(token, { mode });
    setReconLoading(true);
    setReconError('');
    portal.get<{ data: GlReconciliation }>('/api/portal/reconciliation')
      .then((res) => setRecon(res.data))
      .catch(() => setReconError('Failed to load GL reconciliation.'))
      .finally(() => setReconLoading(false));
  }, [token, mode]);

  // Independent, non-fatal fetch — 403 is expected for anonymous/due_diligence viewers and hides
  // the section silently (no banner); any other failure surfaces the standard error+retry state.
  const fetchLabour = useCallback(() => {
    if (!token) return;
    const portal = makePortalInstance(token, { mode });
    setLabourLoading(true);
    setLabourError('');
    setLabourForbidden(false);
    portal.get<{ data: LabourTimesheetData }>('/api/portal/labour-timesheet')
      .then((res) => setLabour(res.data))
      .catch((err: { response?: { status?: number } }) => {
        if (err?.response?.status === 403) {
          setLabourForbidden(true);
          setLabour(null);
        } else {
          setLabourError('Failed to load labour timesheet.');
        }
      })
      .finally(() => setLabourLoading(false));
  }, [token, mode]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchRecon(); }, [fetchRecon]);
  useEffect(() => { fetchLabour(); }, [fetchLabour]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-7 w-48 rounded bg-gray-800 animate-pulse" />
        <div className="h-32 w-full rounded bg-gray-800 animate-pulse" />
      </div>
    );
  }

  if (error) {
    return <InlineError message={error} onRetry={fetchData} />;
  }

  if (!data) return null;

  // GL-reconciled non-labour total: only meaningful once Xero has actually synced.
  const glReconciledTotal = recon && recon.meta.hasSnapshotData
    ? recon.nonLabour.reduce((sum, row) => sum + (row.glAmount ?? 0), 0)
    : null;

  // Non-labour vs Xero derived stats — computed once and shared by the collapsed summary line and
  // the (conditionally rendered) table body/footer, so the figures never diverge between them.
  const nonLabourStats = recon
    ? (() => {
        const hasGl = recon.meta.hasSnapshotData;
        const edisonTotal = recon.nonLabour.reduce((sum, row) => sum + row.edisonAmount, 0);
        const glRows = hasGl ? recon.nonLabour.filter((row) => row.glAmount != null) : [];
        const glTotal = glRows.reduce((sum, row) => sum + (row.glAmount ?? 0), 0);
        const varianceTotal = glRows.reduce((sum, row) => sum + (row.varianceAmount ?? 0), 0);
        // Variance is expressed against the GL total — the same convention as the expanded table's
        // per-row variance column. Dividing by the Edison total overstated it (-59.7% vs -37.4%).
        const variancePct = hasGl && glTotal !== 0 ? (varianceTotal / glTotal) * 100 : null;
        // Count individual vendors (group rows carry their members), not top-level group rows —
        // "1 vendor" under a group of seven reads as missing data to a reviewer.
        const vendorCount = recon.nonLabour.reduce((sum, row) => sum + (row.members?.length ?? 1), 0);
        return { hasGl, edisonTotal, glTotal, varianceTotal, variancePct, vendorCount };
      })()
    : null;

  // Distinct people who contributed R&D labour, claim-wide — derived once for the counts strip.
  // `null` (not 0) when the labour section is absent/gated, so the strip omits the segment rather
  // than showing a misleading zero.
  const peopleCount = labour
    ? new Set(labour.byProject.flatMap((p) => p.contributors.map((c) => c.userId))).size
    : null;

  // Claim-wide person→colour palette for the per-project contribution bars — computed once so a
  // given contributor's colour is identical across every card (spec §8 ambiguity H).
  const personColor = buildPersonPalette(labour);

  // Filter + date-first sort for the *displayed* daily timesheet rows only. The backend returns
  // dailyRows sorted person→date; the CSV export (exportDailyTimesheetCsv below) always walks the
  // raw, unfiltered labour.dailyRows — this derived data is for the on-screen table alone.
  const dailyPersonOptions = labour
    ? [...new Map(labour.dailyRows.map((r): [string, string] => [r.userId, r.name])).entries()]
        .sort((a, b) => displayName(a[1]).localeCompare(displayName(b[1])))
    : [];
  const dailyProjectOptions = labour
    ? [...new Map(labour.dailyRows.map((r): [string, string] => [r.projectId, r.projectName])).entries()]
        .sort((a, b) => a[1].localeCompare(b[1]))
    : [];
  const dailyRowsFiltered = labour
    ? labour.dailyRows.filter((r) =>
        (tsPersonFilter === 'all' || r.userId === tsPersonFilter)
        && (tsProjectFilter === 'all' || r.projectId === tsProjectFilter)
      )
    : [];
  const dailyRowsSorted = [...dailyRowsFiltered].sort((a, b) =>
    a.date === b.date ? displayName(a.name).localeCompare(displayName(b.name)) : a.date.localeCompare(b.date)
  );
  const dailyFilterActive = tsPersonFilter !== 'all' || tsProjectFilter !== 'all';

  // CSV exports — mint a short-lived signed download token, then navigate the browser directly to
  // the CSV endpoint (server-generated: a real browser-issued GET is what makes Safari respect the
  // filename). Only reachable once `labour` is loaded, so these are safe no-ops otherwise (mirrors
  // the full, unfiltered dataset the old client-side export used — filters above only affect the
  // on-screen table). Failure is surfaced via exportError/exportingKind (rendered below), never a
  // silent no-op.
  const mintAndDownloadCsv = (kind: 'labour-timesheet' | 'rate-card') => {
    if (!token) return;
    setExportError('');
    const portal = makePortalInstance(token, { mode });
    portal.post<{ data: { url: string } }>('/api/portal/export-token', { kind })
      .then((res) => {
        // Use window.open (new browsing context) rather than window.location.assign: in grant
        // mode the main window has an active Clerk session and Clerk intercepts cross-domain
        // navigations to validate redirect_url — which the execute-api domain fails. The ?dt=
        // token is self-contained (no Clerk session needed), so opening it in an independent
        // tab bypasses the interception. Content-Disposition: attachment means the tab closes
        // immediately after the download starts.
        window.open(`${apiBase}${res.data.url}`, '_blank', 'noopener,noreferrer');
      })
      .catch(() => {
        setExportingKind(kind);
        setExportError('Failed to prepare the download. Please try again.');
      });
  };

  const exportDailyTimesheetCsv = () => {
    if (!labour) return;
    mintAndDownloadCsv('labour-timesheet');
  };

  const exportRateCardCsv = () => {
    if (!labour) return;
    mintAndDownloadCsv('rate-card');
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-white">R&D Expenditure — {data.incomeYear}</h1>
        <p className="text-sm text-gray-400 mt-1">
          {data.lockedAt
            ? `Figures as lodged (claim locked ${new Date(data.lockedAt).toLocaleDateString('en-AU')}).`
            : 'Draft figures — priced from the rate card.'}
        </p>
      </div>

      {/* Header totals — one primary figure, three quiet supporting figures */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="rounded-lg border border-emerald-900/50 bg-emerald-950/30 p-4">
          <p className="text-xs text-gray-400">Total R&D expenditure</p>
          <p className="text-2xl font-semibold text-emerald-400 mt-1 tabular-nums">{formatCurrency(data.grandTotal)}</p>
          <p className="text-xs text-gray-500 mt-1 tabular-nums">{data.totalRdHours.toFixed(1)} R&D hours</p>
        </div>
        <div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
          <p className="text-xs text-gray-500">Labour</p>
          <p className="text-2xl font-semibold text-white mt-1 tabular-nums">{formatCurrency(data.totalRdExpenditure)}</p>
          <p className="text-xs text-gray-600 mt-1">Capped hours × rate card</p>
        </div>
        <div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
          <p className="text-xs text-gray-500">Non-labour</p>
          <p className="text-2xl font-semibold text-white mt-1 tabular-nums">{formatCurrency(data.totalNonLabourExpenditure)}</p>
          <p className="text-xs text-gray-600 mt-1">Cloud, AI, licences &amp; hardware</p>
        </div>
        <div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
          <p className="text-xs text-gray-500">GL reconciled</p>
          <p className="text-2xl font-semibold text-white mt-1 tabular-nums">
            {glReconciledTotal != null ? formatCurrency(glReconciledTotal) : '—'}
          </p>
          <p className="text-xs text-gray-600 mt-1">Matched to Xero ledger</p>
        </div>
      </div>

      {/* Counts strip — structural project counts, independent of the priced figures above */}
      {data.composition && (
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-lg border border-gray-800 bg-gray-950/30 px-4 py-2.5 text-sm text-gray-300">
          <span>
            <span className="font-semibold text-white tabular-nums">{data.composition.claimedProjectCount}</span> projects in this claim
          </span>
          <span className="text-gray-700" aria-hidden="true">|</span>
          <span>
            <span className="font-semibold text-white tabular-nums">{data.composition.excludedNonRdProjectCount}</span> projects assessed as non-R&amp;D and excluded
          </span>
          {peopleCount != null && (
            <>
              <span className="text-gray-700" aria-hidden="true">|</span>
              <span>
                <span className="font-semibold text-white tabular-nums">{peopleCount}</span> {peopleCount === 1 ? 'person' : 'people'} contributed
              </span>
            </>
          )}
        </div>
      )}

      {/* Verification widgets — stacked full-width. A two-up grid was tried (spec §8.2) and reverted:
          the payroll widget needs the full width for its side-by-side ratio+chart layout, and the
          collapsed Xero row beside it left a tall empty column. The scroll saving comes from the
          Xero table collapsing, not from cramming the two widgets side by side. */}

      {/* Labour vs payroll — payroll-driven (payroll_snapshots), independent of Xero GL snapshot sync.
          Must NOT be gated behind recon.meta.hasSnapshotData — that gate is for the Xero non-labour block only. */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-white">Labour vs payroll</h2>

        {reconLoading ? (
          <div className="h-32 w-full rounded bg-gray-800 animate-pulse" />
        ) : reconError ? (
          <InlineError message={reconError} onRetry={fetchRecon} />
        ) : recon && (recon.labour.payrollTotal > 0 || recon.labour.monthsCovered > 0) ? (
          <div className="rounded-lg border border-gray-800 overflow-hidden">
            {/* Full-width: headline ratio + status pill on the left, 12-month paired bars right. */}
            <div className="px-4 py-4 grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Top block: headline ratio + status pill + supporting figures */}
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-gray-500">% of gross wages claimed</p>
                  <p className="text-3xl font-semibold text-white mt-1 tabular-nums">
                    {recon.labour.ratios.rdVsPayroll != null ? `${(recon.labour.ratios.rdVsPayroll * 100).toFixed(0)}%` : '—'}
                  </p>
                </div>
                {recon.labour.overCapture ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-900/50 bg-amber-950/40 px-2.5 py-1 text-xs text-amber-400">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    Over the payroll ceiling in some months
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-900/50 bg-emerald-950/40 px-2.5 py-1 text-xs text-emerald-400">
                    <CheckCircle2 className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    Within payroll ceiling every month
                  </span>
                )}
                <div className="space-y-1 pt-1">
                  <p className="text-xs text-gray-500 tabular-nums">
                    Gross wages (FY): <span className="text-gray-300">{formatCurrency(recon.labour.payrollTotal)}</span>
                  </p>
                  <p className="text-xs text-gray-500 tabular-nums">
                    Claimed R&D labour: <span className="text-gray-300">{formatCurrency(recon.labour.rdLabour)}</span>
                  </p>
                </div>
              </div>

              {/* Right block: 12-month paired bar chart — gross wages vs captured labour */}
              <div>
                {(() => {
                  const max = Math.max(1, ...recon.labour.byMonth.map((m) => Math.max(m.payroll, m.capturedLabour)));
                  return (
                    <div className="overflow-x-auto">
                      <div
                        role="img"
                        aria-label="Monthly gross wages vs captured labour"
                        className="flex items-end gap-3 min-w-[480px] px-1"
                      >
                        {recon.labour.byMonth.map((m) => {
                          const monthLabel = new Date(`${m.month}-01`).toLocaleDateString('en-AU', { month: 'short' });
                          const payrollHeightPct = (m.payroll / max) * 100;
                          const capturedHeightPct = (m.capturedLabour / max) * 100;
                          return (
                            <div
                              key={m.month}
                              className="flex flex-col items-center gap-1"
                              title={`${monthLabel}: gross wages ${formatCurrency(m.payroll)}, captured labour ${formatCurrency(m.capturedLabour)}`}
                              aria-label={`${monthLabel}: gross wages ${formatCurrency(m.payroll)}, captured labour ${formatCurrency(m.capturedLabour)}`}
                            >
                              <div className="flex items-end gap-0.5 h-32">
                                <div className="w-2.5 bg-gray-600 rounded-t-sm" style={{ height: `${payrollHeightPct}%` }} />
                                <div className="w-2.5 bg-blue-500 rounded-t-sm" style={{ height: `${capturedHeightPct}%` }} />
                              </div>
                              <span className="text-[10px] text-gray-500">{monthLabel}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
                <div className="flex items-center gap-4 mt-2 px-1">
                  <span className="inline-flex items-center gap-1.5 text-xs text-gray-400">
                    <span className="h-2 w-2 rounded-full bg-gray-600" aria-hidden="true" />
                    Gross wages
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-xs text-gray-400">
                    <span className="h-2 w-2 rounded-full bg-blue-500" aria-hidden="true" />
                    Captured labour
                  </span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-xs text-gray-500">Payroll not yet synced from Xero.</p>
        )}
      </div>

      {/* Merged non-labour vendor cost table — Edison figures + live Xero GL comparison. Collapsed
          behind a one-line summary; the full vendor table expands on click. */}
      <div className="rounded-lg border border-gray-800 overflow-hidden">
        <div className="border-b border-gray-800 bg-gray-900 px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-sm font-medium text-white">Non-labour vs Xero ledger</h2>
            {recon && recon.nonLabour.length > 0 && (
              <p className="text-xs text-gray-500 mt-0.5 tabular-nums">
                {nonLabourStats?.vendorCount ?? recon.nonLabour.length} {(nonLabourStats?.vendorCount ?? recon.nonLabour.length) === 1 ? 'vendor' : 'vendors'}
                {nonLabourStats?.hasGl && nonLabourStats.variancePct != null && (
                  <> · variance {nonLabourStats.variancePct >= 0 ? '+' : '-'}{Math.abs(nonLabourStats.variancePct).toFixed(1)}%</>
                )}
              </p>
            )}
          </div>
          {recon && recon.nonLabour.length > 0 && (
            <button
              type="button"
              onClick={() => setXeroOpen((o) => !o)}
              aria-expanded={xeroOpen}
              className="text-xs text-blue-400 hover:text-blue-300 shrink-0"
            >
              {xeroOpen ? 'Collapse' : 'Expand'}
            </button>
          )}
        </div>
        {/* Collapsed-with-data renders NO body at all — an empty padded box under the summary line
            reads as a broken widget. The body exists only for loading/error/empty/expanded states. */}
        {(reconLoading || reconError || (recon && recon.nonLabour.length === 0) || xeroOpen) && (
        <div className="px-4 py-4 space-y-3">
          {reconLoading ? (
            <div className="h-32 w-full rounded bg-gray-800 animate-pulse" />
          ) : reconError ? (
            <InlineError message={reconError} onRetry={fetchRecon} />
          ) : recon && recon.nonLabour.length === 0 ? (
            <p className="text-xs text-gray-600">No non-labour costs recorded for this year.</p>
          ) : recon && xeroOpen ? (
            (() => {
              const hasGl = nonLabourStats?.hasGl ?? false;
              const edisonTotal = nonLabourStats?.edisonTotal ?? 0;
              const glTotal = nonLabourStats?.glTotal ?? 0;
              const varianceTotal = nonLabourStats?.varianceTotal ?? 0;

              return (
                <>
                  {!hasGl && (
                    <AmberStatusLine text="Ledger comparison not yet available — Xero reconciliation hasn't been set up for this claim." />
                  )}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-800">
                          <th className="px-2 py-2 text-left text-xs font-medium text-gray-500">Vendor</th>
                          <th className="px-2 py-2 text-left text-xs font-medium text-gray-500">Category</th>
                          <th className="px-2 py-2 text-left text-xs font-medium text-gray-500">Source</th>
                          <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">Edison $</th>
                          <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">Xero GL $</th>
                          <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">Variance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recon.nonLabour.map((row) => {
                          const glShown = hasGl && row.glAmount != null;
                          const varianceText = glShown && row.varianceAmount != null
                            ? `${row.varianceAmount >= 0 ? '+' : '-'}${formatCurrency(Math.abs(row.varianceAmount))} (${row.variancePct != null ? `${row.variancePct.toFixed(1)}%` : '—'})`
                            : '—';
                          return (
                            <Fragment key={row.vendor}>
                              <tr className="border-b border-gray-800 last:border-b-0">
                                <td className="px-2 py-2 text-gray-300">
                                  {row.vendor === 'unattributed' ? 'Unattributed' : row.vendor}
                                  {row.mappedAccounts.length > 0 ? (
                                    <p className="text-xs text-gray-600 mt-0.5">Matched: {row.mappedAccounts.join(', ')}</p>
                                  ) : row.unmapped ? (
                                    <p className="flex items-center gap-1 text-xs text-amber-400 mt-0.5">
                                      <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden="true" />
                                      {row.vendor === 'unattributed' ? 'no vendor assigned — cannot tie to a GL account' : 'no GL account mapped'}
                                    </p>
                                  ) : null}
                                </td>
                                <td className="px-2 py-2 text-gray-400">{row.category ? (CATEGORY_LABEL[row.category] ?? row.category) : '—'}</td>
                                <td className="px-2 py-2"><SourcePill kind={row.sourceKind} /></td>
                                <td className="px-2 py-2 text-right text-gray-300 tabular-nums">{formatCurrency(row.edisonAmount)}</td>
                                <td className="px-2 py-2 text-right text-gray-300 tabular-nums">{glShown ? formatCurrency(row.glAmount as number) : '—'}</td>
                                <td className="px-2 py-2 text-right text-gray-300 tabular-nums">{varianceText}</td>
                              </tr>
                              {row.members?.map((member) => (
                                <tr key={`${row.vendor}|${member.vendor}`} className="border-b border-gray-800 last:border-b-0">
                                  <td className="px-2 py-2 pl-6 text-gray-500">
                                    <span className="text-gray-600 mr-1" aria-hidden="true">└</span>{member.vendor}
                                  </td>
                                  <td className="px-2 py-2 text-gray-600">—</td>
                                  <td className="px-2 py-2 text-gray-600">—</td>
                                  <td className="px-2 py-2 text-right text-gray-400 tabular-nums">{formatCurrency(member.edisonAmount)}</td>
                                  <td className="px-2 py-2 text-right text-gray-600">—</td>
                                  <td className="px-2 py-2 text-right text-gray-600">—</td>
                                </tr>
                              ))}
                            </Fragment>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="border-t border-gray-700">
                          <td className="px-2 py-2 text-gray-300 font-medium" colSpan={3}>Total</td>
                          <td className="px-2 py-2 text-right text-white font-semibold tabular-nums">{formatCurrency(edisonTotal)}</td>
                          <td className="px-2 py-2 text-right text-white font-semibold tabular-nums">{hasGl ? formatCurrency(glTotal) : '—'}</td>
                          <td className="px-2 py-2 text-right text-white font-semibold tabular-nums">
                            {hasGl ? `${varianceTotal >= 0 ? '+' : '-'}${formatCurrency(Math.abs(varianceTotal))}` : '—'}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  <p className="text-xs text-gray-500">
                    Expected variance causes: usage-month vs invoice-month timing, FX conversion, and provider credits. Figures are whole-bill (not R&D-apportioned).
                  </p>
                  {recon.meta.lastXeroSyncAt && (
                    <p className="text-xs text-gray-600">
                      Xero last synced {new Date(recon.meta.lastXeroSyncAt).toLocaleDateString('en-AU')}.
                    </p>
                  )}
                </>
              );
            })()
          ) : null}
        </div>
        )}
      </div>

      {/* Per-project cards — header (name, determination, totals), Iain's own-words description,
          a per-person contribution bar + legend (advisor/auditor grant only), and a collapsed
          activities table with a category chip per row. Matched to `labour.byProject` by name —
          the snapshot carries no projectId and names are unique within a claim. */}
      <div className="space-y-4">
        {data.projects.map((p) => (
          <ProjectCard
            key={p.name}
            project={p}
            labourProject={labour?.byProject.find((lp) => lp.projectName === p.name)}
            personColor={personColor}
          />
        ))}
      </div>

      {/* R&D labour timesheet + rate card — advisor/auditor GRANT only. 403 renders nothing here
          (no error banner): most viewers of this page (anonymous / due_diligence) are expected to
          be gated out. Any other failure gets the standard error+retry state. */}
      {labourLoading ? (
        <div className="space-y-4">
          <div className="h-6 w-64 rounded bg-gray-800 animate-pulse" />
          <div className="h-40 w-full rounded bg-gray-800 animate-pulse" />
        </div>
      ) : labourForbidden ? null : labourError ? (
        <InlineError message={labourError} onRetry={fetchLabour} />
      ) : labour ? (
        <div className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-white">R&amp;D labour timesheet &amp; rate card</h2>
            <p className="text-sm text-gray-400 mt-1">
              Underlying timesheet data and rate-card composition behind the labour total.
            </p>
          </div>

          {exportError && (
            <InlineError
              message={exportError}
              onRetry={() => exportingKind && mintAndDownloadCsv(exportingKind)}
            />
          )}

          {labour.reconciliation.delta !== 0 && (
            <AmberStatusLine
              text={`Live figure differs from the lodged total by ${formatCurrency(Math.abs(labour.reconciliation.delta))} — entries were edited after lock.`}
            />
          )}

          {/* Per-project contributor detail is now rendered per-card (person contribution bars) —
              see project cards above. This footer keeps only the deeper substantiation detail:
              the daily timesheet and the rate card, both collapsed behind a one-line summary. */}

          {/* Daily timesheet — collapsed row. Exactly five columns when expanded — no
              raw/capped/rd% mechanics columns. Displayed rows are sorted date→person (the backend
              itself returns person→date order; the CSV export below always uses that full,
              unfiltered backend order — it's the substantiation artefact, not the on-screen view). */}
          <div className="rounded-lg border border-gray-800 overflow-hidden">
            <div className="border-b border-gray-800 bg-gray-900 px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <h3 className="text-sm font-medium text-white">Per-person daily timesheet</h3>
                <p className="text-xs text-gray-500 mt-0.5 tabular-nums truncate">
                  {dailyFilterActive
                    ? `${dailyRowsSorted.length} of ${labour.dailyRows.length} rows`
                    : `${labour.dailyRows.length} ${labour.dailyRows.length === 1 ? 'row' : 'rows'}`} · date · person · project · hours · $
                </p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <ExportCsvButton
                  onClick={exportDailyTimesheetCsv}
                  label="Export per-person daily timesheet to CSV (exports all rows)"
                  title="Exports the full timesheet — filters above only affect what's shown on screen"
                />
                {labour.dailyRows.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setTsOpen((o) => !o)}
                    aria-expanded={tsOpen}
                    className="text-xs text-blue-400 hover:text-blue-300"
                  >
                    {tsOpen ? 'Collapse' : 'Expand'}
                  </button>
                )}
              </div>
            </div>
            {tsOpen && (
              <div className="px-4 py-4">
                {labour.dailyRows.length === 0 ? (
                  <p className="text-xs text-gray-600">No daily timesheet rows recorded.</p>
                ) : (
                  <div className="space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <select
                        value={tsPersonFilter}
                        onChange={(e) => setTsPersonFilter(e.target.value)}
                        className={FILTER_SELECT_CLASS}
                        aria-label="Filter timesheet by person"
                      >
                        <option value="all">All people</option>
                        {dailyPersonOptions.map(([userId, name]) => (
                          <option key={userId} value={userId}>{displayName(name)}</option>
                        ))}
                      </select>
                      <select
                        value={tsProjectFilter}
                        onChange={(e) => setTsProjectFilter(e.target.value)}
                        className={FILTER_SELECT_CLASS}
                        aria-label="Filter timesheet by project"
                      >
                        <option value="all">All projects</option>
                        {dailyProjectOptions.map(([projectId, projectName]) => (
                          <option key={projectId} value={projectId}>{projectName}</option>
                        ))}
                      </select>
                      {dailyFilterActive && (
                        <button
                          type="button"
                          onClick={() => { setTsPersonFilter('all'); setTsProjectFilter('all'); }}
                          className="text-xs text-blue-400 hover:text-blue-300"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    {dailyRowsSorted.length === 0 ? (
                      <p className="text-xs text-gray-600">No rows match the current filters.</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-800">
                              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500">Date</th>
                              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500">Person</th>
                              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500">Project</th>
                              <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">Hours</th>
                              <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">$</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dailyRowsSorted.map((r) => (
                              <tr key={`${r.userId}|${r.date}|${r.projectId}`} className="border-b border-gray-800 last:border-b-0">
                                <td className="px-2 py-2 text-gray-400 tabular-nums">{r.date}</td>
                                <td className="px-2 py-2 text-gray-300">{r.name}</td>
                                <td className="px-2 py-2 text-gray-400">{r.projectName}</td>
                                <td className="px-2 py-2 text-right text-gray-300 tabular-nums">{r.rdHours.toFixed(1)}</td>
                                <td className="px-2 py-2 text-right text-gray-300 tabular-nums">{formatCurrency(r.rdExpenditure)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Rate card — collapsed row. No capping/mechanics, just the rate and what it is built from. */}
          <div className="rounded-lg border border-gray-800 overflow-hidden">
            <div className="border-b border-gray-800 bg-gray-900 px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <h3 className="text-sm font-medium text-white">Rate card</h3>
                <p className="text-xs text-gray-500 mt-0.5 truncate">
                  {labour.rateCard.length === 0
                    ? 'No rate card entries recorded.'
                    : labour.rateCard.slice(0, 4).map((r) => `${r.name} ${formatRate(r.hourlyRate)}`).join(' · ') + (labour.rateCard.length > 4 ? ' …' : '')}
                </p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <ExportCsvButton onClick={exportRateCardCsv} label="Export rate card to CSV" />
                {labour.rateCard.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setRateOpen((o) => !o)}
                    aria-expanded={rateOpen}
                    className="text-xs text-blue-400 hover:text-blue-300"
                  >
                    {rateOpen ? 'Collapse' : 'Expand'}
                  </button>
                )}
              </div>
            </div>
            {rateOpen && (
              <div className="px-4 py-4">
                {labour.rateCard.length === 0 ? (
                  <p className="text-xs text-gray-600">No rate card entries recorded.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-800">
                          <th className="px-2 py-2 text-left text-xs font-medium text-gray-500">Person</th>
                          <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">$/hr</th>
                          <th className="px-2 py-2 text-left text-xs font-medium text-gray-500">Composition</th>
                        </tr>
                      </thead>
                      <tbody>
                        {labour.rateCard.map((entry) => (
                          <tr key={entry.userId} className="border-b border-gray-800 last:border-b-0">
                            <td className="px-2 py-2 text-gray-300">{entry.name}</td>
                            <td className="px-2 py-2 text-right text-gray-300 tabular-nums">{formatRate(entry.hourlyRate)}</td>
                            <td className="px-2 py-2"><RateComposition entry={entry} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-3">
                  Rates are fully-loaded employment costs: annual salary plus superannuation plus a
                  pro-rata share of workers compensation insurance, divided by annual hours. No
                  payroll tax component is included — total Australian taxable wages are below the
                  Western Australian payroll tax threshold ($1,000,000), so no payroll tax is
                  payable.
                </p>
              </div>
            )}
          </div>
        </div>
      ) : null}

    </div>
  );
}
